import { Controller } from "@hotwired/stimulus"

// Renders an interactive revenue area chart (Amazon/eBay-seller style) into an
// inline SVG. Data is passed as a JSON value so the server stays the source of
// truth; the controller only handles geometry, the entrance animation, and the
// hover crosshair + tooltip.
//
// Expected data value: [{ date: "Jun 3", label: "Mon", amount: 1234.0 }, ...]
export default class extends Controller {
  static targets = [
    "svg", "area", "line", "marker", "crosshair", "hoverDot",
    "tooltip", "tooltipValue", "tooltipDate"
  ]
  static values = {
    data: Array,
    currency: { type: String, default: "PKR" }
  }

  connect() {
    this.points = []
    this._onResize = () => this.render()
    window.addEventListener("resize", this._onResize)
    // Defer one frame so the SVG has a measured width before we lay out.
    requestAnimationFrame(() => this.render())
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
  }

  // ── layout ──────────────────────────────────────────────────────────────

  render() {
    const data = this.dataValue || []
    if (data.length === 0) return

    const rect = this.svgTarget.getBoundingClientRect()
    const W = rect.width || 600
    const H = rect.height || 140
    const padX = 2
    const padTop = 8
    const padBottom = 4

    this.svgTarget.setAttribute("viewBox", `0 0 ${W} ${H}`)

    const max = Math.max(...data.map(d => d.amount), 1)
    const innerW = W - padX * 2
    const innerH = H - padTop - padBottom
    const stepX = data.length > 1 ? innerW / (data.length - 1) : 0

    this.points = data.map((d, i) => ({
      x: padX + i * stepX,
      y: padTop + innerH - (d.amount / max) * innerH,
      ...d
    }))

    const linePath = this._smoothPath(this.points)
    const areaPath = `${linePath} L ${this.points[this.points.length - 1].x},${H} L ${this.points[0].x},${H} Z`

    this.lineTarget.setAttribute("d", linePath)
    this.areaTarget.setAttribute("d", areaPath)

    // End-of-line marker (the "where we are now" dot).
    const last = this.points[this.points.length - 1]
    this.markerTarget.setAttribute("cx", last.x)
    this.markerTarget.setAttribute("cy", last.y)

    this._animateIn()
  }

  // Catmull-Rom → cubic Bézier for a smooth, professional curve.
  _smoothPath(pts) {
    if (pts.length < 2) return ""
    let d = `M ${pts[0].x},${pts[0].y}`
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i - 1] || pts[i]
      const p1 = pts[i]
      const p2 = pts[i + 1]
      const p3 = pts[i + 2] || p2
      const t = 0.18
      const c1x = p1.x + (p2.x - p0.x) * t
      const c1y = p1.y + (p2.y - p0.y) * t
      const c2x = p2.x - (p3.x - p1.x) * t
      const c2y = p2.y - (p3.y - p1.y) * t
      d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`
    }
    return d
  }

  _animateIn() {
    const len = this.lineTarget.getTotalLength()
    this.lineTarget.style.transition = "none"
    this.lineTarget.style.strokeDasharray = len
    this.lineTarget.style.strokeDashoffset = len
    this.areaTarget.style.opacity = "0"
    // Force reflow, then animate.
    this.lineTarget.getBoundingClientRect()
    this.lineTarget.style.transition = "stroke-dashoffset 1.1s cubic-bezier(.4,0,.2,1)"
    this.lineTarget.style.strokeDashoffset = "0"
    this.areaTarget.style.transition = "opacity .9s ease .25s"
    this.areaTarget.style.opacity = "1"
  }

  // ── interaction ─────────────────────────────────────────────────────────

  move(event) {
    if (this.points.length === 0) return
    const rect = this.svgTarget.getBoundingClientRect()
    const clientX = event.touches ? event.touches[0].clientX : event.clientX
    const x = ((clientX - rect.left) / rect.width) * this._viewBoxWidth()

    // Nearest point.
    let nearest = this.points[0]
    let best = Infinity
    for (const p of this.points) {
      const dist = Math.abs(p.x - x)
      if (dist < best) { best = dist; nearest = p }
    }

    this.crosshairTarget.setAttribute("x1", nearest.x)
    this.crosshairTarget.setAttribute("x2", nearest.x)
    this.crosshairTarget.style.opacity = "1"
    this.hoverDotTarget.setAttribute("cx", nearest.x)
    this.hoverDotTarget.setAttribute("cy", nearest.y)
    this.hoverDotTarget.style.opacity = "1"

    this.tooltipValueTarget.textContent =
      `${this.currencyValue} ${Math.round(nearest.amount).toLocaleString()}`
    this.tooltipDateTarget.textContent = nearest.date

    // Position tooltip in DOM pixels, clamped within the card.
    const pxPerUnit = rect.width / this._viewBoxWidth()
    const tip = this.tooltipTarget
    tip.style.opacity = "1"
    const tipW = tip.offsetWidth || 110
    let left = nearest.x * pxPerUnit
    left = Math.max(tipW / 2, Math.min(left, rect.width - tipW / 2))
    tip.style.left = `${left}px`
  }

  leave() {
    this.crosshairTarget.style.opacity = "0"
    this.hoverDotTarget.style.opacity = "0"
    this.tooltipTarget.style.opacity = "0"
  }

  _viewBoxWidth() {
    const vb = this.svgTarget.getAttribute("viewBox")
    return vb ? parseFloat(vb.split(" ")[2]) : this.svgTarget.getBoundingClientRect().width
  }
}
